import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { Button, StatusDot } from '@/components/atoms';
import { metricsApi } from '@/services/metrics';

import { computeSeoScore } from './seoScore';
import styles from './SeoPanel.module.css';

interface SeoPanelProps {
  title: string;
  excerpt: string;
  contentHtml: string;
  featuredMediaId: number | null;
  slug?: string | null;
  /** PAA real do SERP (origem do artigo) — R6: cada pergunta vira H3 com 1 clique. */
  paa?: string[];
  /**
   * When the editor wants to react to PAA picks it passes this
   * callback — the panel only owns the keyword input + score.
   */
  onInsertH3?: (question: string) => void;
  /** Aplica a correção da IA (título/meta) direto nos campos do editor. */
  onApply?: (patch: { title?: string; excerpt?: string }) => void;
  /** B-27: aplica o corpo revisado pela IA no editor (o humano ainda salva). */
  onApplyContent?: (contentHtml: string) => void;
}

// B-27: os 4 presets do pedido — o resto é instrução livre (mesmas travas).
const TEXT_PRESETS: { key: string; label: string }[] = [
  { key: 'mais_curto', label: 'Mais curto' },
  { key: 'mais_popular', label: 'Mais popular' },
  { key: 'mais_contexto', label: '+ Contexto' },
  { key: 'corrigir_seo', label: 'Corrigir SEO' },
];

export function SeoPanel({
  title,
  excerpt,
  contentHtml,
  featuredMediaId,
  slug,
  paa,
  onInsertH3,
  onApply,
  onApplyContent,
}: SeoPanelProps) {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  void t;
  const [keyword, setKeyword] = useState('');
  const [paaQuery, setPaaQuery] = useState('');
  const [preset, setPreset] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');

  const assist = useMutation({
    mutationFn: () =>
      metricsApi.seoAssist({ title, content: contentHtml, keyword: keyword || undefined }),
  });

  const textAssist = useMutation({
    mutationFn: () =>
      metricsApi.textAssist({
        title,
        content: contentHtml,
        preset: preset ?? undefined,
        instruction: freeText || undefined,
      }),
  });

  const result = useMemo(
    () =>
      computeSeoScore({
        title,
        excerpt,
        contentHtml,
        featuredMediaId,
        slug,
        keyword,
      }),
    [title, excerpt, contentHtml, featuredMediaId, slug, keyword],
  );

  const scoreTone =
    result.score >= 80 ? 'ok' : result.score >= 50 ? 'warn' : 'crit';

  return (
    <aside className={styles.root} aria-label="SEO panel">
      <header className={styles.head}>
        <h2 className={styles.title}>SEO</h2>
        <div className={`${styles.score} ${styles[`score-${scoreTone}`]}`}>
          <span className={styles.scoreValue}>{result.score}</span>
          <span className={styles.scoreMax}>/100</span>
        </div>
      </header>

      <div className={styles.bar}>
        <div
          className={styles.barFill}
          style={{
            width: `${result.score}%`,
            background:
              scoreTone === 'ok'
                ? 'var(--ok)'
                : scoreTone === 'warn'
                  ? 'var(--warn)'
                  : 'var(--crit)',
          }}
        />
      </div>

      <ul className={styles.checks}>
        {result.checks.map((c) => (
          <li key={c.key} className={styles.check}>
            <StatusDot
              state={
                c.status === 'ok' ? 'ok' : c.status === 'warn' ? 'warn' : 'crit'
              }
            />
            <span className={styles.checkLabel}>{c.label}</span>
            {c.detail && (
              <span className={styles.checkDetail}>{c.detail}</span>
            )}
          </li>
        ))}
      </ul>

      <div className={styles.section}>
        <label className={styles.fieldLabel}>Keyword focal</label>
        <input
          className={styles.input}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="ex.: ebola Congo"
        />
        <p className={styles.hint}>
          Ativa checks de keyword no título / 1.º parágrafo / density.
        </p>
      </div>

      <div className={styles.section}>
        <label className={styles.fieldLabel}>Correção prévia com IA</label>
        <p className={styles.hint}>
          Antes de publicar — a IA sugere título + meta mirando a keyword (e dicas on-page).
        </p>
        <Button
          size="sm"
          variant="secondary"
          loading={assist.isPending}
          onClick={() => assist.mutate()}
        >
          Sugerir título / meta
        </Button>

        {assist.data?.tips && assist.data.tips.length > 0 && (
          <ul className={styles.tips}>
            {assist.data.tips.map((t, i) => (
              <li key={i} className={styles.tip}>{t}</li>
            ))}
          </ul>
        )}

        {assist.data?.variations.map((v, i) => (
          <div key={i} className={styles.variation}>
            <p className={styles.varTitle}>{v.title} <small>({v.title.length}c)</small></p>
            <p className={styles.varMeta}>{v.meta} <small>({v.meta.length}c)</small></p>
            {onApply && (
              <button
                type="button"
                className={styles.useBtn}
                onClick={() => onApply({ title: v.title, excerpt: v.meta })}
              >
                Usar este (título + meta)
              </button>
            )}
          </div>
        ))}
      </div>

      {onApplyContent && (
        <div className={styles.section}>
          <label className={styles.fieldLabel}>Melhorar texto</label>
          <p className={styles.hint}>
            Reescreve o corpo sob as travas: data ancorada e fidelidade — a IA
            nunca acrescenta fato. Você revisa antes de salvar.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
            {TEXT_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={styles.useBtn}
                style={preset === p.key ? { fontWeight: 700, textDecoration: 'underline' } : undefined}
                onClick={() => setPreset(preset === p.key ? null : p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <input
            className={styles.input}
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="ou instrua com suas palavras…"
          />
          <div style={{ marginTop: 6 }}>
            <Button
              size="sm"
              variant="secondary"
              loading={textAssist.isPending}
              disabled={!preset && !freeText.trim()}
              onClick={() => textAssist.mutate()}
            >
              Melhorar texto
            </Button>
          </div>
          {textAssist.isError && (
            <p className={styles.hint}>Erro: {String(textAssist.error)}</p>
          )}
          {textAssist.data && (
            <div className={styles.variation}>
              <p className={styles.varMeta}>
                Corpo revisado pronto ({textAssist.data.content_html.length.toLocaleString('pt-BR')}{' '}
                caracteres). Aplicar substitui o texto no editor — nada é salvo
                até você salvar.
              </p>
              <button
                type="button"
                className={styles.useBtn}
                onClick={() => {
                  onApplyContent(textAssist.data.content_html);
                  textAssist.reset();
                }}
              >
                Aplicar no editor
              </button>
            </div>
          )}
        </div>
      )}

      <div className={styles.section}>
        <label className={styles.fieldLabel}>People Also Asked</label>
        {paa && paa.length > 0 ? (
          <>
            <p className={styles.hint}>
              Perguntas reais do público (SERP, da origem do artigo) — clica para
              inserir como H3:
            </p>
            {paa.map((q) => (
              <button
                key={q}
                type="button"
                className={styles.addH3}
                onClick={() => onInsertH3?.(q)}
                disabled={!onInsertH3}
              >
                + {q}
              </button>
            ))}
          </>
        ) : (
          <>
            <p className={styles.hint}>
              Sem PAA da origem. Adiciona uma pergunta manualmente:
            </p>
            <input
              className={styles.input}
              value={paaQuery}
              onChange={(e) => setPaaQuery(e.target.value)}
              placeholder="pergunta do leitor…"
            />
            {paaQuery.trim() && onInsertH3 && (
              <button
                type="button"
                className={styles.addH3}
                onClick={() => onInsertH3(paaQuery.trim())}
              >
                + Adicionar &quot;{paaQuery.trim()}&quot; como H3
              </button>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
